import { setDefaultRequest } from '@/data/Content/DefaultStore';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useSettings } from '@/data/Settings';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { StoreDetails } from '@/data/types/Store';
import { useCallback } from 'react';
import { MAX_EXP_TIME, MIN_EXP_TIME } from 'shared/custom/constants/commonConstants';
import { StoreData } from 'shared/custom/types/commonTypes';
import { buildStoreDeliveryLocationCookie } from 'shared/custom/utils/cookieHandler';

export const useStoreSwitcher = (allStoresData: StoreData[]) => {
	const { settings } = useSettings();
	const params = useExtraRequestParameters();

	const {
		actions: { selectStore },
	} = useStoreLocatorState();

	const defaultStoreExpiryTime =
		parseInt(settings.userData['DefaultStoreExpiryTime'], 10) || MIN_EXP_TIME;
	const switchStore = useCallback(
		async (stateId: string, physicalStoreId: string, marketId: string, isExternal = false) => {
			if (!physicalStoreId) return;
			try {
				await setDefaultRequest(
					{ defaultStore: physicalStoreId, fromPageName: '2' },
					settings.storeId,
					settings.defaultLanguage,
					defaultStoreExpiryTime,
					params
				);

				const newStoreData = allStoresData.find((s: StoreData) => s.stLocId === physicalStoreId);
				if (newStoreData) {
					const { stLocName, latitude, longitude, address1, address2, city, zipcode } =
						newStoreData;
					selectStore({
						...newStoreData,
						id: physicalStoreId,
						physicalStoreName: stLocName,
						storeFullAddress: `${address1}, ${address2}, ${city}`,
						coordinates: { lat: +latitude, lng: +longitude },
						attributes: [],
						isExternal,
					} as StoreDetails);
					// Only assigns the delivery location while the customer has not set a zip code of
					// their own: switching store must not change a delivery zip code they entered.
					buildStoreDeliveryLocationCookie(
						{ zipCode: zipcode, locality: city, state: stLocName },
						MAX_EXP_TIME
					);
				}
			} catch (error) {
				console.error('Failed to switch store:', error);
			}
		},
		[
			settings.storeId,
			settings.defaultLanguage,
			allStoresData,
			selectStore,
			defaultStoreExpiryTime,
			params,
		]
	);

	return { switchStore };
};
